
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";

interface EditPatientFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: any;
}

export const EditPatientFileDialog = ({ open, onOpenChange, file }: EditPatientFileDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    birth_date: "",
    email: "",
    phone_number: "",
    gender: "",
    marital_status: "",
    height: "",
    weight: "",
    blood_type: "",
    treating_doctor: "",
    insurance_provider: "",
    smoker: false,
    cigarettes_per_day: "",
    children_count: "",
    social_security_number: "",
    address: "",
    city: "",
    postal_code: "",
    country: "",
    notes: "", // Ajout du champ notes
  });

  // Load file data when dialog opens
  useEffect(() => {
    if (file && open) {
      setFormData({
        last_name: file.last_name || "",
        first_name: file.first_name || "",
        birth_date: file.birth_date || "",
        email: file.email || "",
        phone_number: file.phone_number || "",
        gender: file.gender || "",
        marital_status: file.marital_status || "",
        height: file.height?.toString() || "",
        weight: file.weight?.toString() || "",
        blood_type: file.blood_type || "",
        treating_doctor: file.treating_doctor || "",
        insurance_provider: file.insurance_provider || "",
        smoker: file.smoker || false,
        cigarettes_per_day: file.cigarettes_per_day?.toString() || "",
        children_count: file.children_count?.toString() || "",
        social_security_number: file.social_security_number || "",
        address: file.address || "",
        city: file.city || "",
        postal_code: file.postal_code || "",
        country: file.country || "",
        notes: file.notes || "", // Initialisation du champ notes
      });
    }
  }, [file, open]);

  const handleChange = (field: string, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create an object with actual values to update in Supabase
      const patientFileData = {
        last_name: formData.last_name,
        first_name: formData.first_name,
        birth_date: formData.birth_date || null,
        email: formData.email || null,
        phone_number: formData.phone_number || null,
        gender: formData.gender || null,
        marital_status: formData.marital_status || null,
        height: formData.height ? Number(formData.height) : null,
        weight: formData.weight ? Number(formData.weight) : null,
        blood_type: formData.blood_type || null,
        treating_doctor: formData.treating_doctor || null,
        insurance_provider: formData.insurance_provider || null,
        smoker: formData.smoker || false,
        cigarettes_per_day: formData.cigarettes_per_day ? Number(formData.cigarettes_per_day) : null,
        children_count: formData.children_count ? Number(formData.children_count) : null,
        social_security_number: formData.social_security_number || null,
        address: formData.address || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        country: formData.country || null,
        notes: formData.notes || null, // Ajout du champ notes dans l'objet à mettre à jour
      };

      // Update the patient file in Supabase
      const { error } = await supabase
        .from("patient_files")
        .update(patientFileData)
        .eq("id", file.id);

      if (error) throw error;

      toast({
        title: "Dossier mis à jour",
        description: "Les modifications ont été enregistrées avec succès",
      });

      queryClient.invalidateQueries({ queryKey: ["patient-files"] });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating patient file:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le dossier patient: " + error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const maritalStatuses = ['Célibataire', 'Concubin/e', 'Pacs', 'Marié/e', 'Séparé/e', 'Divorcé/e', 'Veuf/ve'];
  const genders = [
    { value: 'M', label: 'Masculin' },
    { value: 'F', label: 'Féminin' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-center py-2 bg-primary/10 rounded-md">
            Modifier la fiche - {file?.file_number}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="last_name">Nom*</Label>
              <Input
                required
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="first_name">Prénom*</Label>
              <Input
                required
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="birth_date">Date de naissance</Label>
              <Input
                type="date"
                id="birth_date"
                value={formData.birth_date}
                onChange={(e) => handleChange('birth_date', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="gender">Sexe</Label>
              <Select value={formData.gender} onValueChange={(value) => handleChange('gender', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {genders.map(gender => (
                    <SelectItem key={gender.value} value={gender.value}>{gender.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="marital_status">Situation familiale</Label>
              <Select value={formData.marital_status} onValueChange={(value) => handleChange('marital_status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {maritalStatuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="children_count">Nombre d'enfants</Label>
              <Input
                type="number"
                id="children_count"
                min="0"
                value={formData.children_count}
                onChange={(e) => handleChange('children_count', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="social_security_number">Numéro de sécurité sociale</Label>
              <Input
                type="text"
                id="social_security_number"
                placeholder="Ex: 1234567890123"
                value={formData.social_security_number}
                onChange={(e) => handleChange('social_security_number', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="insurance_provider">Mutuelle</Label>
              <Input
                type="text"
                id="insurance_provider"
                value={formData.insurance_provider}
                onChange={(e) => handleChange('insurance_provider', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="height">Taille (cm)</Label>
              <Input
                type="number"
                id="height"
                min="0"
                value={formData.height}
                onChange={(e) => handleChange('height', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="weight">Poids (kg)</Label>
              <Input
                type="number"
                id="weight"
                min="0"
                step="0.1"
                value={formData.weight}
                onChange={(e) => handleChange('weight', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="blood_type">Groupe sanguin</Label>
              <Select value={formData.blood_type} onValueChange={(value) => handleChange('blood_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {bloodTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="treating_doctor">Médecin traitant</Label>
              <Input
                type="text"
                id="treating_doctor"
                value={formData.treating_doctor}
                onChange={(e) => handleChange('treating_doctor', e.target.value)}
              />
            </div>
            
            <div className="grid w-full items-center gap-1.5">
              <div className="flex items-center space-x-2 mt-4">
                <Checkbox 
                  id="smoker" 
                  checked={formData.smoker}
                  onCheckedChange={(checked) => handleChange('smoker', !!checked)} 
                />
                <label
                  htmlFor="smoker"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Fumeur
                </label>
              </div>
              
              <div className={cn("transition-all duration-200", formData.smoker ? "opacity-100" : "opacity-50 pointer-events-none")}>
                <Label htmlFor="cigarettes_per_day">Cigarettes par jour</Label>
                <Input
                  type="number"
                  id="cigarettes_per_day"
                  min="0"
                  value={formData.cigarettes_per_day}
                  onChange={(e) => handleChange('cigarettes_per_day', e.target.value)}
                  disabled={!formData.smoker}
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="phone_number">Téléphone</Label>
              <Input
                type="tel"
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="address">Adresse</Label>
              <Input
                type="text"
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="city">Ville</Label>
              <Input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="postal_code">Code postal</Label>
              <Input
                type="text"
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="country">Pays</Label>
              <Input
                type="text"
                id="country"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </div>
          </div>
          
          {/* Ajout du champ notes */}
          <div className="grid grid-cols-1 gap-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Informations supplémentaires..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Modification..." : "Enregistrer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
